import React, { useState, useEffect } from 'react';
import { X, Search, CheckCircle, User, Users as UsersIcon } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { LoadingSpinner } from '../ui/Loading';
import { charactersService } from '../../services';
import { getClassTypeIcon } from "../../utils/characterTranslation";

const BulkConfirmParticipantsModal = ({ isOpen, onClose, event, onConfirm, existingParticipants = [] }) => {
  const [allCharacters, setAllCharacters] = useState([]);
  const [filteredCharacters, setFilteredCharacters] = useState([]);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAllCharacters();
      setSelectedCharacters([]);
      setSearchTerm('');
      setClassFilter('ALL');
    }
  }, [isOpen]);

  useEffect(() => {
    filterCharacters();
  }, [searchTerm, classFilter, allCharacters]);

  const loadAllCharacters = async () => {
    try {
      setLoading(true);
      const response = await charactersService.getAllCharacters({ limit: 1000 });
      
      // Filter out characters already confirmed in the event
      const existingCharacterIds = existingParticipants
        .filter(p => p.status === 'CONFIRMED')
        .map(p => p.characterId);
      
      const availableCharacters = (response.characters || [])
        .filter(char => !existingCharacterIds.includes(char.id))
        .filter(char => char.status === 'ACTIVE'); // Only active characters
      
      setAllCharacters(availableCharacters);
      setFilteredCharacters(availableCharacters);
    } catch (err) {
      console.error('Error loading characters:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterCharacters = () => {
    let filtered = [...allCharacters];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(char => 
        char.name.toLowerCase().includes(term) ||
        char.user?.name.toLowerCase().includes(term)
      );
    }

    // Filter by class
    if (classFilter !== 'ALL') {
      filtered = filtered.filter(char => char.class === classFilter);
    }

    setFilteredCharacters(filtered);
  };

  const toggleCharacter = (characterId) => {
    setSelectedCharacters(prev => 
      prev.includes(characterId)
        ? prev.filter(id => id !== characterId)
        : [...prev, characterId]
    );
  };

  const toggleAll = () => {
    if (selectedCharacters.length === filteredCharacters.length) {
      setSelectedCharacters([]);
    } else {
      setSelectedCharacters(filteredCharacters.map(char => char.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedCharacters.length === 0) return;

    setSubmitting(true);
    try {
      await onConfirm(selectedCharacters);
      onClose();
    } catch (err) {
      console.error('Error confirming participants:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const classes = ['ALL', 'GUERREIRA', 'ASSASSINA', 'ARQUEIRA', 'LUTADOR', 'PIKE', 'MECANICO', 'XAMA', 'SACERDOTISA', 'CAVALEIRO', 'MAGO', 'ATALANTA'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-cyan-500/20">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <UsersIcon className="w-7 h-7" />
                Confirmar Participantes
              </h2>
              <p className="text-cyan-100 mt-1">
                {event?.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-cyan-200 transition-colors"
            >
              <X size={28} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 bg-slate-900/50 border-b border-white/10 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Buscar
              </label>
              <Input
                type="text"
                placeholder="Nome do personagem ou jogador..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Filtrar por Classe
              </label>
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
              >
                {classes.map(cls => (
                  <option key={cls} value={cls}>
                    {cls === 'ALL' ? 'Todas as Classes' : cls}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              {selectedCharacters.length} de {filteredCharacters.length} selecionados
            </div>
            <button
              onClick={toggleAll}
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              {selectedCharacters.length === filteredCharacters.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
            </button>
          </div>
        </div>

        {/* Characters List */}
        <div className="p-6 overflow-y-auto max-h-96">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredCharacters.length === 0 ? (
            <div className="text-center py-12">
              <User className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                {allCharacters.length === 0 
                  ? 'Todos os personagens já estão confirmados ou não há personagens disponíveis'
                  : 'Nenhum personagem encontrado com os filtros aplicados'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredCharacters.map((character) => {
                const isSelected = selectedCharacters.includes(character.id);
                return (
                  <div
                    key={character.id}
                    onClick={() => toggleCharacter(character.id)}
                    className={`cursor-pointer p-4 rounded-lg border transition-all duration-200 ${
                      isSelected
                        ? 'bg-cyan-500/20 border-cyan-500/50'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-cyan-500 border-cyan-500'
                          : 'border-gray-600'
                      }`}>
                        {isSelected && <CheckCircle className="w-5 h-5 text-white" />}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base font-semibold text-white truncate">
                            {character.name}
                          </span>
                          <span className="text-sm text-gray-400">
                            Lvl {character.level}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                          <span>{getClassTypeIcon(character.class)}</span>
                          <span>{character.class}</span>
                        </div>
                        
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <User className="w-3 h-3" />
                          <span className="truncate">{character.user?.name || 'Desconhecido'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-900 p-6 border-t border-white/10 flex justify-between items-center gap-4">
          <div className="text-sm text-gray-400">
            💡 <strong>Dica:</strong> Clique nos personagens para selecionar/desmarcar
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={selectedCharacters.length === 0 || submitting}
            >
              {submitting ? 'Confirmando...' : `Confirmar ${selectedCharacters.length} Participante${selectedCharacters.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkConfirmParticipantsModal;
